export function formatPhoneNumberForDisplay(value: string | null | undefined): string | null {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();

  if (trimmed.startsWith('+') && !trimmed.startsWith('+54')) {
    return formatInternationalNumber(trimmed.replace(/\D/g, ''));
  }

  const digits = trimmed.replace(/\D/g, '');
  const argentinaDigits = digits.startsWith('54') ? digits.slice(2) : digits;
  const nationalDigits = argentinaDigits.startsWith('9') && argentinaDigits.length === 11
    ? argentinaDigits.slice(1)
    : argentinaDigits;

  if (nationalDigits.length === 10) {
    const areaCodeLength = nationalDigits.startsWith('11') ? 2 : 3;
    const areaCode = nationalDigits.slice(0, areaCodeLength);
    const subscriberNumber = nationalDigits.slice(areaCodeLength);

    return `+54 ${areaCode} ${formatSubscriberNumber(subscriberNumber)}`;
  }

  const e164Match = /^\+54(9?)(\d{2,4})(\d{6,8})$/.exec(trimmed);

  if (!e164Match) {
    return trimmed;
  }

  const mobilePrefix = e164Match[1];
  const areaCode = e164Match[2];
  const subscriberNumber = e164Match[3];

  return `+54 ${mobilePrefix ? '9 ' : ''}${areaCode} ${formatSubscriberNumber(subscriberNumber)}`;
}

export function formatPhoneNumberWhileTyping(value: string | null | undefined): string {
  if (!value?.trim()) {
    return '';
  }

  const digits = value.replace(/\D/g, '');

  if (value.trimStart().startsWith('+') && !digits.startsWith('54')) {
    return digits ? formatInternationalNumber(digits) : '+';
  }

  const argentinaDigits = digits.startsWith('54') ? digits.slice(2) : digits;
  const nationalDigits = argentinaDigits.startsWith('9') && argentinaDigits.length > 10
    ? argentinaDigits.slice(1)
    : argentinaDigits;

  if (!nationalDigits) {
    return '';
  }

  const areaCodeLength = nationalDigits.startsWith('11') ? 2 : 3;
  const areaCode = nationalDigits.slice(0, Math.min(areaCodeLength, nationalDigits.length));
  const subscriberNumber = nationalDigits.slice(areaCodeLength, 10);

  if (!subscriberNumber) {
    return areaCode;
  }

  return `+54 ${areaCode} ${formatSubscriberNumber(subscriberNumber)}`;
}

function formatSubscriberNumber(subscriberNumber: string): string {
  return subscriberNumber.length > 4
    ? `${subscriberNumber.slice(0, -4)}-${subscriberNumber.slice(-4)}`
    : subscriberNumber;
}

function formatInternationalNumber(digits: string): string {
  if (!digits) {
    return '+';
  }

  const countryFormats: Array<{ code: string; groups: number[] }> = [
    { code: '55', groups: [2, 5, 4] },
    { code: '56', groups: [1, 4, 4] },
    { code: '57', groups: [3, 3, 4] },
    { code: '58', groups: [3, 3, 4] },
    { code: '51', groups: [3, 3, 3] },
    { code: '591', groups: [3, 4, 4] },
    { code: '592', groups: [3, 4] },
    { code: '593', groups: [2, 3, 4] },
    { code: '594', groups: [3, 2, 2, 2] },
    { code: '595', groups: [3, 3, 3] },
    { code: '597', groups: [3, 4] },
    { code: '598', groups: [2, 3, 3] },
  ];

  const format = countryFormats.find(item => digits.startsWith(item.code));

  if (format) {
    const nationalNumber = digits.slice(format.code.length);

    if (!nationalNumber) {
      return `+${format.code}`;
    }

    return `+${format.code} ${formatByGroups(nationalNumber, format.groups)}`;
  }

  return `+${digits}`;
}

function formatByGroups(value: string, groups: number[]): string {
  const formattedGroups: string[] = [];
  let currentIndex = 0;

  for (const groupLength of groups) {
    if (currentIndex >= value.length) {
      break;
    }

    formattedGroups.push(value.slice(currentIndex, currentIndex + groupLength));
    currentIndex += groupLength;
  }

  if (currentIndex < value.length) {
    formattedGroups.push(value.slice(currentIndex));
  }

  return formattedGroups.join(' ');
}
